import { useEffect, useState } from "react";

// Shared loader for the live chain-comparison endpoints
// (netlify/functions/chain-tx-compare.ts and nft-tx-live.ts).

export interface ChainDaily {
  id: string;
  name: string;
  daily: number;
  sampled: string;
}

export interface ChainStatsResponse {
  fetchedAt: number;
  chains: ChainDaily[];
  errors: string[];
  source: "cache" | "fresh" | "stale-cache";
}

const REFRESH_MS = 15 * 60 * 1000; // backend re-samples hourly

export function useChainStats(endpoint: string) {
  const [data, setData] = useState<ChainStatsResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    function load() {
      fetch(endpoint)
        .then((res) => {
          if (!res.ok) throw new Error(`status ${res.status}`);
          return res.json();
        })
        .then((json: ChainStatsResponse) => {
          if (cancelled) return;
          if (!Array.isArray(json?.chains) || json.chains.length === 0) throw new Error("no data");
          setData(json);
          setError(false);
        })
        .catch(() => {
          if (!cancelled) setError(true);
        });
    }
    load();
    const interval = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [endpoint]);

  return { data, error };
}

export function formatCount(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function formatUpdated(ms: number): string {
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
}
